import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, ArrowRight, Phone, Mail, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { TagChip } from '../tags/TagChip';

type SortField = 'name' | 'score' | 'amount' | 'age' | 'operator';
type SortDir = 'asc' | 'desc';

interface Stage {
  stage_code: string;
  label: string;
  color: string;
  allowed_transitions: string[];
}

interface KanbanListViewProps {
  stages: Stage[];
  getAppsByStage: (stageCode: string) => any[];
  statusField: string;
  showLoanAmount?: boolean;
  onCardClick: (id: number) => void;
  onStatusChange: (appId: number, newStatus: string) => void;
  bulkMode?: boolean;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  terminology?: { plural: string };
}

export const KanbanListView: React.FC<KanbanListViewProps> = ({
  stages,
  getAppsByStage,
  statusField,
  showLoanAmount,
  onCardClick,
  onStatusChange,
  bulkMode,
  selectedIds,
  onToggleSelect,
  terminology,
}) => {
  const [expandedStages, setExpandedStages] = useState<Set<string>>(() => {
    // Auto-expand stages that have applications (max 3)
    const withApps = stages.filter(s => getAppsByStage(s.stage_code).length > 0);
    return new Set(withApps.slice(0, 3).map(s => s.stage_code));
  });

  const [moveMenuApp, setMoveMenuApp] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  };

  const sortApps = (apps: any[]) => {
    if (!sortField) return apps;
    return [...apps].sort((a, b) => {
      let va: any, vb: any;
      switch (sortField) {
        case 'name': va = `${a.first_name} ${a.last_name}`.toLowerCase(); vb = `${b.first_name} ${b.last_name}`.toLowerCase(); break;
        case 'score': va = a.lead_score || 0; vb = b.lead_score || 0; break;
        case 'amount': va = Number(showLoanAmount ? a.loan_amount : a.requested_amount) || 0; vb = Number(showLoanAmount ? b.loan_amount : b.requested_amount) || 0; break;
        case 'age': va = new Date(a.created_at).getTime(); vb = new Date(b.created_at).getTime(); break;
        case 'operator': va = (a.assigned_operator_name || '').toLowerCase(); vb = (b.assigned_operator_name || '').toLowerCase(); break;
        default: return 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={10} className="text-gray-300" />;
    return sortDir === 'asc' ? <ArrowUp size={10} className="text-blue-500" /> : <ArrowDown size={10} className="text-blue-500" />;
  };

  const toggleStage = (code: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const totalApps = stages.reduce((sum, s) => sum + getAppsByStage(s.stage_code).length, 0);

  return (
    <div className="space-y-2 px-1">
      {/* Summary */}
      <div className="text-xs text-gray-500 px-2 pb-1">
        {totalApps} {terminology?.plural || 'wniosków'} w {stages.length} etapach
      </div>

      {stages.map(stage => {
        const apps = getAppsByStage(stage.stage_code);
        const isExpanded = expandedStages.has(stage.stage_code);

        return (
          <div key={stage.stage_code} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Stage header */}
            <button
              type="button"
              onClick={() => toggleStage(stage.stage_code)}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${stage.color}`} />
                <span className="font-medium text-sm text-gray-800">{stage.label}</span>
                <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-medium">
                  {apps.length}
                </span>
              </div>
              {isExpanded ? (
                <ChevronDown size={16} className="text-gray-400" />
              ) : (
                <ChevronRight size={16} className="text-gray-400" />
              )}
            </button>

            {/* Expanded cards */}
            {isExpanded && apps.length > 0 && (
              <div className="border-t border-gray-100">
                {/* Sort header */}
                <div className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 border-b text-[10px] text-gray-500 font-medium select-none">
                  {bulkMode && <div className="w-4" />}
                  <button type="button" onClick={() => toggleSort('name')} className="flex-1 flex items-center gap-1 hover:text-gray-700">Nazwa <SortIcon field="name" /></button>
                  <button type="button" onClick={() => toggleSort('score')} className="flex items-center gap-1 hover:text-gray-700 w-10">Score <SortIcon field="score" /></button>
                  <button type="button" onClick={() => toggleSort('operator')} className="flex items-center gap-1 hover:text-gray-700 w-16">Operator <SortIcon field="operator" /></button>
                  <button type="button" onClick={() => toggleSort('amount')} className="flex items-center gap-1 hover:text-gray-700 w-20 justify-end">Kwota <SortIcon field="amount" /></button>
                  <button type="button" onClick={() => toggleSort('age')} className="flex items-center gap-1 hover:text-gray-700 w-8">Wiek <SortIcon field="age" /></button>
                  <div className="w-14" />
                  <div className="w-7" />
                </div>
                {sortApps(apps).map(app => {
                  const daysOld = Math.floor((Date.now() - new Date(app.created_at).getTime()) / 86400000);
                  const ageColor = daysOld < 7 ? 'text-green-600' : daysOld < 14 ? 'text-yellow-600' : 'text-red-600';
                  const amount = showLoanAmount ? app.loan_amount : app.requested_amount;
                  const currentStage = stages.find(s => s.stage_code === stage.stage_code);
                  const transitions = currentStage?.allowed_transitions || [];
                  const showMoveMenu = moveMenuApp === app.id;

                  return (
                    <div
                      key={app.id}
                      className="relative border-b border-gray-50 last:border-b-0"
                    >
                      <div
                        onClick={() => {
                          if (bulkMode && onToggleSelect) {
                            onToggleSelect(app.id);
                          } else {
                            onCardClick(app.id);
                          }
                        }}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                          selectedIds?.has(app.id) ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-blue-50'
                        }`}
                      >
                        {/* Bulk checkbox */}
                        {bulkMode && (
                          <input
                            type="checkbox"
                            checked={!!selectedIds?.has(app.id)}
                            onChange={() => onToggleSelect?.(app.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                            aria-label={`Zaznacz ${app.first_name} ${app.last_name}`}
                          />
                        )}
                        {/* Name & ID */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-900 truncate">
                              {app.first_name} {app.last_name}
                            </span>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">#{app.id}</span>
                          </div>
                          {app.service_type === 'wycieczki' ? (
                            <p className="text-[11px] text-gray-500 truncate mt-0.5">
                              {app.wycieczki_route && <span className="text-teal-600">{app.wycieczki_route}</span>}
                              {app.wycieczki_group_size && <span className="ml-1">{app.wycieczki_group_size} os.</span>}
                              {app.wycieczki_preferred_date && <span className="ml-1">{new Date(app.wycieczki_preferred_date).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}</span>}
                            </p>
                          ) : app.business_idea ? (
                            <p className="text-[11px] text-gray-500 truncate mt-0.5">{app.business_idea}</p>
                          ) : null}
                          {/* Tags */}
                          {app.tags && app.tags.length > 0 && (
                            <div className="flex flex-wrap gap-0.5 mt-1">
                              {app.tags.slice(0, 2).map((tag: any) => (
                                <TagChip key={tag.id} tag={tag} size="sm" />
                              ))}
                              {app.tags.length > 2 && (
                                <span className="text-[10px] text-gray-400">+{app.tags.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Lead score badge */}
                        {app.lead_score && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                            app.lead_score >= 75 ? 'bg-emerald-100 text-emerald-700' :
                            app.lead_score >= 50 ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-500'
                          }`}>
                            {app.lead_score}
                          </span>
                        )}

                        {/* Operator */}
                        {app.assigned_operator_name && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0 max-w-[60px] truncate" title={app.assigned_operator_name}>
                            {app.assigned_operator_name.split(' ')[0]}
                          </span>
                        )}

                        {/* Amount */}
                        {amount && (
                          <span className="text-xs font-medium text-gray-700 flex-shrink-0">
                            {Number(amount).toLocaleString('pl-PL')} zł
                          </span>
                        )}

                        {/* Age + date */}
                        <span className={`text-[11px] font-medium flex-shrink-0 ${ageColor}`}
                          title={`Utwórzono: ${new Date(app.created_at).toLocaleDateString('pl-PL')}${app.updated_at ? '\nAktualiz.: ' + new Date(app.updated_at).toLocaleDateString('pl-PL') : ''}`}>
                          {daysOld}d
                        </span>

                        {/* Contact icons */}
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {app.phone && (
                            <a
                              href={`tel:${app.phone}`}
                              onClick={e => e.stopPropagation()}
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              aria-label={`Zadzwoń ${app.phone}`}
                            >
                              <Phone size={12} />
                            </a>
                          )}
                          {app.email && (
                            <a
                              href={`mailto:${app.email}`}
                              onClick={e => e.stopPropagation()}
                              className="p-1 text-gray-400 hover:text-blue-600 rounded"
                              aria-label={`Napisz ${app.email}`}
                            >
                              <Mail size={12} />
                            </a>
                          )}
                        </div>

                        {/* Move button */}
                        {transitions.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveMenuApp(showMoveMenu ? null : app.id);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-lg flex-shrink-0 transition-colors"
                            aria-label="Przenieś do innego etapu"
                          >
                            <ArrowRight size={14} />
                          </button>
                        )}
                      </div>

                      {/* Move dropdown */}
                      {showMoveMenu && transitions.length > 0 && (
                        <div className="absolute right-2 top-full z-20 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px]">
                          <div className="px-3 py-1.5 text-[10px] text-gray-400 uppercase tracking-wider font-medium">
                            Przenieś do:
                          </div>
                          {transitions.map(targetCode => {
                            const targetStage = stages.find(s => s.stage_code === targetCode);
                            if (!targetStage) return null;
                            return (
                              <button
                                key={targetCode}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onStatusChange(app.id, targetCode);
                                  setMoveMenuApp(null);
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                              >
                                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${targetStage.color}`} />
                                {targetStage.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty state */}
            {isExpanded && apps.length === 0 && (
              <div className="border-t border-gray-100 px-3 py-4 text-center text-xs text-gray-400">
                Brak {terminology?.plural || 'wniosków'} w tym etapie
              </div>
            )}
          </div>
        );
      })}

      {/* Close move menu on outside click */}
      {moveMenuApp !== null && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setMoveMenuApp(null)}
        />
      )}
    </div>
  );
};
